import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiWithAuth } from '../../lib/api';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon,
  LifebuoyIcon,
  PaperAirplaneIcon,
  QuestionMarkCircleIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftEllipsisIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const Contact = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    category: '',
    subject: '',
    message: '',
    email: user?.email || ''
  });
  const [loading, setLoading] = useState(false);

  const categories = [
    { value: 'general', label: '一般的なお問い合わせ', icon: QuestionMarkCircleIcon },
    { value: 'technical', label: 'アプリの不具合・技術的問題', icon: ExclamationTriangleIcon },
    { value: 'account', label: 'アカウント・ログインに関して', icon: ChatBubbleLeftEllipsisIcon },
    { value: 'payment', label: '報酬・支払いに関して', icon: InformationCircleIcon },
    { value: 'work', label: '勤務・事業者とのトラブル', icon: ExclamationTriangleIcon },
    { value: 'suggestion', label: 'サービス改善のご提案', icon: InformationCircleIcon }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.subject || !formData.message) {
      toast.error('すべての項目を入力してください。');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      const payload = {
        category: formData.category,
        subject: formData.subject,
        message: formData.message,
        email: formData.email,
        user_id: user?.id,
        user_name: user?.name
      };
      const candidates = [
        '/support/contact',
        '/contact',
        '/support/tickets',
        '/support/messages'
      ];
      let ok = false;
      for (const path of candidates) {
        try {
          const res = await client.post(path, payload);
          if (res && res.status >= 200 && res.status < 300) { ok = true; break; }
        } catch (_) { /* try next */ }
      }
      if (!ok) {
        // Fallback: open mail client with prefilled content to contact admins
        const supportEmail = 'support@byters.jp';
        const subj = encodeURIComponent(formData.subject + '（お問い合わせ）');
        const body = encodeURIComponent(
          `種類: ${formData.category}\n` +
          `差出人: ${formData.user_name || ''} (${formData.email})\n` +
          `ユーザーID: ${user?.id || ''}\n\n` +
          `${formData.message}`
        );
        const link = document.createElement('a');
        link.href = `mailto:${supportEmail}?subject=${subj}&body=${body}`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
        ok = true;
      }
      
      toast.success('お問い合わせを送信しました。回答までお待ちください。');
      
      // フォームをリセット
      setFormData({
        category: '',
        subject: '',
        message: '',
        email: user?.email || ''
      });
      
      // 送信完了後、マイページに戻る
      setTimeout(() => {
        navigate('/mypage');
      }, 1500);
      
    } catch (error) {
      toast.error('送信に失敗しました。しばらく時間をおいて再度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">お問い合わせ</h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 案内文 */}
        <div className="bg-blue-50 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <LifebuoyIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold text-blue-900 mb-2">サポートチームがお手伝いします</h2>
              <p className="text-blue-800 text-sm leading-relaxed">
                バイターズに関するご質問やお困りごとがございましたら、お気軽にお問い合わせください。
                通常、1-2営業日以内にご返信いたします。緊急の場合は、メールでのご連絡もご利用ください。
              </p>
              <div className="mt-3 text-blue-800 text-sm">
                <p><strong>サポート受付時間：</strong> 平日 10:00-18:00</p>
              </div>
            </div>
          </div>
        </div>

        {/* よくある質問へのリンク */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">お問い合わせの前に</h3>
          <p className="text-gray-600 mb-4">
            よくある質問で解決できる場合があります。まずはこちらをご確認ください。
          </p>
          <button
            onClick={() => navigate('/mypage/faq')}
            className="flex items-center text-blue-600 hover:text-blue-700 font-medium"
          >
            <QuestionMarkCircleIcon className="w-5 h-5 mr-2" />
            よくある質問を見る
          </button>
        </div>

        {/* お問い合わせフォーム */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">お問い合わせフォーム</h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* カテゴリ選択 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                お問い合わせの種類 <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 gap-3">
                {categories.map((category) => {
                  const IconComponent = category.icon;
                  return (
                    <label
                      key={category.value}
                      className={`relative flex items-center p-4 cursor-pointer rounded-lg border-2 transition-colors ${
                        formData.category === category.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="category"
                        value={category.value}
                        checked={formData.category === category.value}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <IconComponent className="w-5 h-5 mr-3 text-gray-400" />
                      <span className={`font-medium ${
                        formData.category === category.value ? 'text-blue-700' : 'text-gray-900'
                      }`}>
                        {category.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* メールアドレス */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">回答をお送りするメールアドレスです。</p>
            </div>

            {/* 件名 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                件名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="お問い合わせの件名を入力してください"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* お問い合わせ内容 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                お問い合わせ内容 <span className="text-red-500">*</span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={8}
                placeholder="お困りの内容を詳しくお書きください。&#10;&#10;・いつ頃から問題が発生しているか&#10;・どのような操作を行った際に問題が起きたか&#10;・エラーメッセージが表示される場合はその内容&#10;&#10;などの詳細をご記載いただくと、より迅速にサポートできます。"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                問題の詳細をできるだけ具体的にお書きください（文字数制限なし）。
              </p>
            </div>

            {/* 送信ボタン */}
            <button
              type="submit"
              disabled={loading || !formData.category || !formData.subject || !formData.message}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  送信中...
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                  お問い合わせを送信
                </>
              )}
            </button>
          </form>
        </div>

        {/* 注意事項 */}
        <div className="mt-8 bg-gray-50 rounded-lg p-6">
          <h4 className="font-medium text-gray-900 mb-3">ご注意事項</h4>
          <ul className="text-sm text-gray-600 space-y-2">
            <li> 土日祝日および当社指定の休業日はサポート業務をお休みしております。</li>
            <li> お問い合わせ内容によっては、回答にお時間をいただく場合があります。</li>
            <li> 緊急性の高いセキュリティ問題は、メールでのご連絡をお願いします。</li>
            <li> 個人情報に関するお問い合わせは、本人確認のため追加情報をお聞きする場合があります。</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Contact;